import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";
import { generateOrderPDF } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, FileDown, ImagePlus, X, Loader2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const STATUSES = ["baru", "dikirim", "dipesan", "diterima"];
const photoUrl = (p) => `${API}/files/${p.file_id}/download`;

const empty = {
  date: new Date().toISOString().slice(0, 10),
  requester: "", supplier: "", needed_by: "", priority: "normal",
  items: [], images: [], notes: "", status: "baru",
};
const emptyItem = { sparepart_id: "", name: "", code: "", qty: 1, unit: "", note: "", image: null };

const waText = (o) => {
  const lines = [];
  lines.push("*PERMINTAAN ORDER SPAREPART*");
  lines.push(`No. Order: ${o.order_no || "-"}`);
  lines.push(`Tanggal: ${formatDate(o.date)}`);
  if (o.requester) lines.push(`Pemohon: ${o.requester}`);
  if (o.supplier) lines.push(`Supplier: ${o.supplier}`);
  if (o.needed_by) lines.push(`Dibutuhkan: ${formatDate(o.needed_by)}`);
  lines.push(`Prioritas: ${o.priority === "mendesak" ? "MENDESAK" : "Normal"}`);
  lines.push("");
  lines.push("Daftar Item:");
  (o.items || []).forEach((it, i) =>
    lines.push(`${i + 1}. ${it.name} - ${it.qty} ${it.unit || ""}${it.note ? ` (${it.note})` : ""}`.trim())
  );
  if (o.notes) { lines.push(""); lines.push(`Catatan: ${o.notes}`); }
  lines.push("");
  lines.push("_Mohon diproses. Foto/PDF order dapat dilampirkan terpisah._");
  return lines.join("\n");
};

export default function SparepartOrders() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [partSel, setPartSel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const imgInputRef = useRef(null);
  const imgItemIdxRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const load = () => api.get("/sparepart-orders").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/spareparts").then((r) => setParts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const prefill = location.state?.prefillItems;
    if (!prefill?.length || !isAdmin) { return; }
    setForm({ ...empty, items: prefill.map((p) => ({ ...emptyItem, sparepart_id: p.id, name: p.name, code: p.code || "", qty: "", unit: p.unit || "", note: `Stok ${p.stock ?? 0} / min ${p.min_stock ?? 0}` })) });
    setEditId(null); setOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, isAdmin, navigate, location.pathname]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (o) => {
    setForm({ ...empty, ...o, items: (o.items || []).map((i) => ({ ...emptyItem, ...i })), images: o.images || [] });
    setEditId(o.id); setOpen(true);
  };

  const addFromStock = () => {
    const p = parts.find((x) => x.id === partSel);
    if (!p) return;
    setForm((f) => ({
      ...f,
      items: [...f.items, { sparepart_id: p.id, name: p.name, code: p.code || "", qty: 1, unit: p.unit || "", note: "" }],
    }));
    setPartSel("");
  };
  const addManual = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const updItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  const delItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const triggerItemImage = (idx) => { imgItemIdxRef.current = idx; imgInputRef.current?.click(); };
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    const idx = imgItemIdxRef.current;
    if (imgInputRef.current) imgInputRef.current.value = "";
    if (!file || idx == null) return;
    if (!file.type.startsWith("image/")) { toast.error("Hanya berkas gambar yang diperbolehkan"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Ukuran gambar melebihi 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    setUploading(true);
    try {
      const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updItem(idx, "image", { file_id: data.id, filename: data.original_filename, content_type: data.content_type });
      toast.success("Gambar ditambahkan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah gambar");
    } finally {
      setUploading(false);
    }
  };
  const removeItemImage = (idx) => updItem(idx, "image", null);

  const save = async () => {
    if (!form.items.length) { toast.error("Tambahkan minimal satu item sparepart"); return; }
    if (form.items.some((it) => !String(it.name).trim())) { toast.error("Nama sparepart tidak boleh kosong"); return; }
    if (form.items.some((it) => !(Number(it.qty) > 0))) { toast.error("Isi qty setiap item (minimal 1)"); return; }
    const payload = { ...form, items: form.items.map((it) => ({ ...it, qty: Number(it.qty) })) };
    try {
      if (editId) { await api.put(`/sparepart-orders/${editId}`, payload); toast.success("Order diperbarui"); }
      else { await api.post("/sparepart-orders", payload); toast.success("Order dibuat"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeStatus = async (o, status) => {
    try {
      await api.put(`/sparepart-orders/${o.id}/status`, { status }); load();
      const linked = (o.items || []).some((it) => it.sparepart_id);
      if (status === "diterima" && linked) toast.success("Status Diterima — stok sparepart otomatis ditambahkan");
      else if (o.status === "diterima" && linked) toast.success("Status diperbarui — stok sparepart dikurangi kembali");
      else toast.success("Status diperbarui");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/sparepart-orders/${delId}`); toast.success("Order dihapus"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openWhatsApp = (o) => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(waText(o))}`, "_blank");

  return (
    <div>
      <PageHeader title="Order Sparepart" subtitle="Permintaan pembelian sparepart untuk bagian Purchasing.">
        {isAdmin && (
          <Button onClick={openNew} data-testid="add-order-button" className="bg-sky-600 hover:bg-sky-500">
            <Plus className="w-4 h-4 mr-2" /> Buat Order
          </Button>
        )}
      </PageHeader>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="orders-table">
            <TableHeader>
              <TableRow>
                <TableHead>No. Order</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Pemohon</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Prioritas</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Belum ada order sparepart.</TableCell></TableRow>
              )}
              {items.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell className="font-mono font-semibold whitespace-nowrap">{o.order_no}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(o.date)}</TableCell>
                  <TableCell>{o.requester || "-"}</TableCell>
                  <TableCell className="whitespace-nowrap">{(() => { const n = (o.items || []).filter((it) => it.image).length + (o.images || []).length; return `${(o.items || []).length} item${n ? ` · ${n} foto` : ""}`; })()}</TableCell>
                  <TableCell>
                    {o.priority === "mendesak"
                      ? <span className="text-xs font-bold text-rose-600 uppercase">Mendesak</span>
                      : <span className="text-xs text-slate-500">Normal</span>}
                  </TableCell>
                  <TableCell>
                    {isAdmin ? (
                      <Select value={o.status} onValueChange={(v) => changeStatus(o, v)}>
                        <SelectTrigger className="h-8 w-[130px]" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : <StatusBadge status={o.status} />}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openWhatsApp(o)} data-testid={`wa-order-${o.id}`} title="Kirim via WhatsApp">
                      <WhatsAppIcon className="w-4 h-4 text-emerald-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => generateOrderPDF(o)} data-testid={`pdf-order-${o.id}`} title="Unduh PDF">
                      <FileDown className="w-4 h-4 text-sky-600" />
                    </Button>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(o)} data-testid={`edit-order-${o.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDelId(o.id)} data-testid={`delete-order-${o.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> {editId ? "Edit Order Sparepart" : "Buat Order Sparepart"}</DialogTitle><DialogDescription>Isi detail permintaan pembelian sparepart untuk dikirim ke bagian Purchasing.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Tanggal</Label><Input type="date" value={form.date?.slice(0, 10)} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="order-date-input" /></div>
            <div><Label>Dibutuhkan Tanggal</Label><Input type="date" value={form.needed_by?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, needed_by: e.target.value })} data-testid="order-neededby-input" /></div>
            <div><Label>Pemohon</Label><Input value={form.requester} onChange={(e) => setForm({ ...form, requester: e.target.value })} placeholder="Nama pemohon" data-testid="order-requester-input" /></div>
            <div><Label>Supplier (opsional)</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Nama supplier" data-testid="order-supplier-input" /></div>
            <div>
              <Label>Prioritas</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="order-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="mendesak">Mendesak</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Items */}
            <div className="col-span-2 border-t pt-4">
              <Label>Item Sparepart</Label>
              <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="order-image-input" />
              <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                <Select value={partSel} onValueChange={setPartSel}>
                  <SelectTrigger className="flex-1" data-testid="order-part-select"><SelectValue placeholder="Pilih sparepart dari stok" /></SelectTrigger>
                  <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (stok {p.stock})</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={addFromStock} data-testid="order-add-stock-button">Tambah dari Stok</Button>
                <Button type="button" variant="outline" onClick={addManual} data-testid="order-add-manual-button"><Plus className="w-4 h-4 mr-1" /> Manual</Button>
              </div>
              <div className="overflow-x-auto border rounded-lg mt-3">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800 text-xs uppercase text-slate-500">
                    <tr><th className="p-2 text-left min-w-[160px]">Nama</th><th className="p-2 text-left w-24">Gambar</th><th className="p-2 text-left w-20">Kode</th><th className="p-2 text-left w-16">Qty</th><th className="p-2 text-left w-20">Satuan</th><th className="p-2 text-left min-w-[120px]">Keterangan</th><th className="p-2 w-10"></th></tr>
                  </thead>
                  <tbody>
                    {form.items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-4">Tambahkan item sparepart.</td></tr>}
                    {form.items.map((it, idx) => (
                      <tr key={idx} className="border-t align-top" data-testid={`order-item-row-${idx}`}>
                        <td className="p-2"><Input value={it.name} onChange={(e) => updItem(idx, "name", e.target.value)} className="h-9" placeholder="Nama sparepart" data-testid={`order-item-name-${idx}`} /></td>
                        <td className="p-2">
                          {it.image ? (
                            <div className="relative inline-block">
                              <img src={photoUrl(it.image)} alt="" className="w-12 h-12 object-cover rounded border cursor-pointer" onClick={() => setLightbox(photoUrl(it.image))} data-testid={`order-item-image-${idx}`} />
                              <button type="button" onClick={() => removeItemImage(idx)} className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white rounded-full p-0.5 shadow" data-testid={`order-item-image-remove-${idx}`}><X className="w-3 h-3" /></button>
                            </div>
                          ) : (
                            <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => triggerItemImage(idx)} disabled={uploading} data-testid={`order-item-image-add-${idx}`}>
                              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                        </td>
                        <td className="p-2"><Input value={it.code} onChange={(e) => updItem(idx, "code", e.target.value)} className="h-9" /></td>
                        <td className="p-2"><Input type="number" min="1" value={it.qty} onChange={(e) => updItem(idx, "qty", e.target.value)} className={`h-9 ${it.qty === "" ? "border-amber-400" : ""}`} placeholder="Qty" data-testid={`order-item-qty-${idx}`} /></td>
                        <td className="p-2"><Input value={it.unit} onChange={(e) => updItem(idx, "unit", e.target.value)} className="h-9" placeholder="pcs" /></td>
                        <td className="p-2"><Input value={it.note} onChange={(e) => updItem(idx, "note", e.target.value)} className="h-9" /></td>
                        <td className="p-2"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delItem(idx)} data-testid={`order-item-del-${idx}`}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Gambar opsional per item (hanya gambar, maks. 2 MB) — akan tampil pada baris nama di PDF.</p>
            </div>

            <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="order-notes-input" placeholder="Keterangan tambahan untuk purchasing" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="save-order-button" className="bg-sky-600 hover:bg-sky-500">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="order-lightbox">
          <img src={lightbox} alt="pratinjau" className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button className="absolute top-4 right-4 bg-white/90 rounded-full p-1.5" onClick={() => setLightbox(null)}><X className="w-5 h-5" /></button>
        </div>
      )}

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus order ini?</AlertDialogTitle><AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-500" data-testid="confirm-delete-order">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
